import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Button, Form, FormGroup, Modal, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { postPortfolio } from '../../../controllers/product-service';
import { PortfolioResultsV2 } from '../../../controllers/product-service/types';
import { selectCurrentOrgId } from '../../../reducers/profile/profileSlice';
import './UploadItemsModal.css';
import UploadItemsModalSummary from './UploadItemsModalSummary';
import { AsyncState } from '../../../utils/webRequests.type';
import { hasGrants } from '../../../controllers/auth';
import sendRumError from '../../../utils/datadogRum';
import { HelpLinkButton } from '../helpModal/helpModal';

function UploadItemsModal(): JSX.Element {
  const [show, setShow] = useState<boolean>(false);
  const [portfolioResults, setPortfolioResults] = useState<PortfolioResultsV2>(
    {
      new_products: [],
      changed_products: [],
      unchanged_product_count: 0,
      incompatible_products: [],
    },
  );
  const [portfolioFile, setPortfolioFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<AsyncState>('uninitialized');
  const [disableUpload, setDisableUpload] = useState<boolean>(false);
  const [displaySummaryModal, setDisplaySummaryModal] = useState<boolean>(false);
  const currentOrgId = useSelector(selectCurrentOrgId) as number;
  const fileInputRefForm = useRef(null);
  const reset = () => {
    setUploadError(false);
    setPortfolioFile(null);
    (fileInputRefForm.current as unknown as HTMLFormElement).reset();
    setUploadStatus('uninitialized');
  };

  useEffect(() => {
    setUploadStatus('uninitialized');
  }, [portfolioFile]);

  useEffect(() => {
    if (displaySummaryModal && show) {
      setShow(false);
      setUploadStatus('uninitialized');
    }
  }, [displaySummaryModal]);

  const handleSubmit = () => {
    setUploadStatus('loading');
    const formData = new FormData();
    formData.append('file', portfolioFile as File);
    postPortfolio(currentOrgId, formData)
      .then((portfolioResponse) => {
        const portfolioData = portfolioResponse.data;
        setPortfolioResults({
          new_products: portfolioData.new_products,
          changed_products: portfolioData.changed_products,
          unchanged_product_count: portfolioData.unchanged_product_count,
          incompatible_products: portfolioData.incompatible_products,
        });
        setDisplaySummaryModal(true);
      })
      .catch((error) => {
        sendRumError(error);
        setUploadStatus('uninitialized');
        setUploadError(true);
        try {
          if (typeof error.response.data.detail !== 'undefined') {
            const message = `${error.response.data.detail}\nIf you have further questions, please email support at `;
            setUploadMessage(message);
          } else {
            setUploadMessage('Please email support at ');
          }
        } catch {
          setUploadMessage('Please email support at ');
        }
      })
      .finally(() => {
        setPortfolioFile(null);
      });
  };

  const handleClose = () => {
    setShow(false);
    reset();
  };

  useEffect(() => {
    setDisableUpload(uploadStatus !== 'uninitialized'
      || portfolioFile == null
      || uploadError);
  }, [uploadStatus, portfolioFile, uploadError]);

  const isAllowedToUpdate = hasGrants(currentOrgId, ['PRODUCT_ADMIN']);

  return (
    <>
      <div
        id="upload-item-modal-container-button"
        onClick={() => isAllowedToUpdate && setShow(true)}
        role="button"
        tabIndex={0}
        className="pull-right"
      >
        <span>
          {isAllowedToUpdate ? (
            <Button>
              Upload Item Changes
            </Button>
          ) : (
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 100, hide: 400 }}
              overlay={(
                <Tooltip
                  id="upload-tooltip"
                  data-testid="upload-tooltip"
                  className="upload-tooltip"
                >
                  You do not have the permissions required to upload item changes.
                  Please contact an administrator if you need your permissions updated.
                </Tooltip>
              )}
            >
              <span
                className="disabled-button-wrapper"
              >
                <Button
                  disabled
                  className="not-allowed-to-use"
                >
                  Upload Item Changes
                </Button>
              </span>
            </OverlayTrigger>
          )}
        </span>
      </div>
      {
        portfolioResults
        && (
          <UploadItemsModalSummary
            data={portfolioResults}
            setDisplayModal={setDisplaySummaryModal}
            displayModal={displaySummaryModal}
          />
        )
      }

      <Modal size="lg" className="upload-item-modal-container col-5 mt-5" show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Item Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Please select a file containing items to add or change.
            This file must match the Marshall Insights .xlsx template,
            which can be downloaded by clicking the Add/Edit Items button.
            <br />
            <br />
            Once selected, a summary of pending changes will be given before any action is taken.
          </p>
          <Form
            id="File-Input-Form"
            ref={fileInputRefForm}
          >
            <FormGroup>
              <Form.Control
                data-testid="file-upload-input"
                id="file-upload-input"
                placeholder="Browse file(s)"
                type="file"
                name="fileInput"
                multiple={false}
                accept=".xlsx"
                disabled={uploadStatus === 'loading'}
                onChange={(e) => {
                  const files = (e.currentTarget as HTMLInputElement).files as FileList;
                  e.preventDefault();
                  setPortfolioFile(files[0]);
                  setUploadError(false);
                }}
              />
            </FormGroup>
          </Form>
          <hr />

          {uploadError
            && (
              <Alert variant="danger" className="w-100 mt-3 mb-0">
                <span
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {uploadMessage}
                </span>
                <HelpLinkButton />
                .
              </Alert>
            )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            variant="primary"
            id="item-upload-modal-submit-button"
            data-testid="item-upload-modal-submit-button"
            onClick={handleSubmit}
            className="m-1"
            disabled={disableUpload}
            type="submit"
          >
            {
              uploadStatus === 'uninitialized' && <>Submit</>
            }
            {
              uploadStatus === 'loading' && (
                <div
                  className="loading-page-text text-center loading-status"
                >
                  Working
                  <div
                    className="ellipsis-wrapper text-left"
                  >
                    <div
                      className="ellipsis"
                    />
                  </div>
                </div>
              )
            }
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default UploadItemsModal;
